import Phaser from 'phaser';
import {
  socketManager,
  WorldTickData,
  MapData,
  GameEntity,
  BuildingData,
  BuildingStateData,
} from '../socket/SocketManager';
import { IsoMap } from '../iso/IsoMap';
import { IsoBuilding } from '../iso/IsoBuilding';
import { IsoVehicle } from '../iso/IsoVehicle';
import { IsoPedestrian } from '../iso/IsoPedestrian';
import { HUD } from '../ui/HUD';
import { Minimap } from '../ui/Minimap';
import { SidePanel } from '../ui/SidePanel';

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2.5;
const ZOOM_SPEED = 0.08;
const DRAG_THRESHOLD = 6;

export class GameScene extends Phaser.Scene {
  public isoMap!: IsoMap;
  public isoBuildings: IsoBuilding[] = [];
  private vehicles = new Map<string, IsoVehicle>();
  private pedestrians = new Map<string, IsoPedestrian>();
  public hud!: HUD;
  private minimap!: Minimap;
  public sidePanel!: SidePanel;
  private dayOverlay!: Phaser.GameObjects.Rectangle;
  private uiCamera!: Phaser.Cameras.Scene2D.Camera;

  // Interpolation
  private lastTickTime = 0;
  private tickInterval = 1000;

  // Camera drag
  private pointerWasDown = false;
  private dragStartPointerX = 0;
  private dragStartPointerY = 0;
  private dragStartScrollX = 0;
  private dragStartScrollY = 0;
  private dragTotalDistance = 0;
  private dragBlocked = false; // true when pointer is over UI

  // World bounds (cached)
  private worldBounds = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  // State (loaded from server in create())
  public moneyDirty = 0;
  public moneyClean = 0;
  private pendingBuildingState: BuildingStateData | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  async create(): Promise<void> {
    // Load player state from server
    const playerState = await socketManager.fetchPlayerState();
    if (playerState) {
      this.moneyDirty = playerState.moneyDirty;
      this.moneyClean = playerState.moneyClean;
    }

    const mapData: MapData = await socketManager.fetchMap();

    // UI camera — never zooms/scrolls, for HUD/panels/overlays
    this.uiCamera = this.cameras.add(0, 0, this.scale.width, this.scale.height, false, 'ui');
    this.uiCamera.setScroll(0, 0);

    this.isoMap = new IsoMap(this, mapData.tiles, mapData.gridWidth, mapData.gridHeight);

    for (const building of mapData.buildings) {
      const isoBuilding = new IsoBuilding(this, building, this.isoMap);
      this.isoBuildings.push(isoBuilding);
    }

    // Day/night overlay
    this.dayOverlay = this.add
      .rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0)
      .setOrigin(0)
      .setDepth(999);

    this.hud = new HUD(this);
    this.hud.updateMoney(this.moneyDirty, this.moneyClean);

    this.minimap = new Minimap(
      this, mapData.tiles, mapData.buildings, mapData.gridWidth, mapData.gridHeight,
    );

    this.sidePanel = new SidePanel(this);
    this.sidePanel.onEnter((building: BuildingData) => {
      this.enterBuilding(building);
    });
    this.sidePanel.onBuy((building: BuildingData) => {
      this.buyBuilding(building);
    });

    // Camera layers
    this.setupCameraLayers();

    // Cache world bounds
    this.worldBounds = this.isoMap.getWorldBounds();

    // Center camera on the visual center of the iso diamond
    // The diamond center is at grid (gridW/2, gridH/2)
    const isoCenter = this.isoMap.gridToIso(15, 15);
    this.cameras.main.scrollX = isoCenter.x - this.scale.width / 2;
    this.cameras.main.scrollY = isoCenter.y - this.scale.height / 2;
    this.cameras.main.setZoom(1);

    // Zoom via wheel
    this.input.on('wheel', (
      pointer: Phaser.Input.Pointer,
      _go: Phaser.GameObjects.GameObject[],
      _dx: number,
      deltaY: number,
    ) => {
      const cam = this.cameras.main;
      const oldZoom = cam.zoom;
      const factor = deltaY > 0 ? (1 - ZOOM_SPEED) : (1 + ZOOM_SPEED);
      const newZoom = Phaser.Math.Clamp(oldZoom * factor, MIN_ZOOM, MAX_ZOOM);
      if (newZoom === oldZoom) return;

      const wx = cam.scrollX + pointer.x / oldZoom;
      const wy = cam.scrollY + pointer.y / oldZoom;
      cam.setZoom(newZoom);
      cam.scrollX = wx - pointer.x / newZoom;
      cam.scrollY = wy - pointer.y / newZoom;
      this.clampCamera();
    });

    // Socket listeners
    socketManager.onTick((data: WorldTickData) => {
      this.handleTick(data);
    });

    socketManager.onActionResult((result) => {
      if (result.newState) {
        this.moneyDirty = result.newState.moneyDirty;
        this.moneyClean = result.newState.moneyClean;
        this.hud.updateMoney(this.moneyDirty, this.moneyClean);
      }
      if (result.data && (result.data as Record<string, unknown>)['building']) {
        this.pendingBuildingState = result.data as unknown as BuildingStateData;
        const bd = this.pendingBuildingState.building;
        if (bd && this.sidePanel.isVisible()) {
          this.sidePanel.open(bd, this.pendingBuildingState);
        }
      }
    });

    socketManager.onBusinessClosed((data) => {
      console.warn('Business closed by police:', data);
    });

    // When resuming from InteriorScene, reset camera zoom
    this.events.on('resume', () => {
      this.cameras.main.setZoom(1);
      this.clampCamera();
    });
  }

  private setupCameraLayers(): void {
    // UI objects: only on uiCamera (not affected by zoom)
    const uiOnly = [
      this.dayOverlay,
      ...this.hud.getGameObjects(),
      ...this.minimap.getGameObjects(),
    ];
    for (const obj of uiOnly) {
      this.cameras.main.ignore(obj);
    }

    // World objects: only on main camera
    const allObjects = this.children.list;
    for (const obj of allObjects) {
      if (!uiOnly.includes(obj)) {
        this.uiCamera.ignore(obj);
      }
    }

    // SidePanel stays on MAIN camera (so buttons receive input)
    // It uses setScrollFactor(0) to stay in screen space
  }

  // ─── Helpers ───────────────────────────────────────────────

  private isPointerOverPanel(px: number): boolean {
    if (!this.sidePanel.isVisible()) return false;
    return px > this.scale.width - 280;
  }

  private clampCamera(): void {
    const cam = this.cameras.main;
    const b = this.worldBounds;
    const viewW = cam.width / cam.zoom;
    const viewH = cam.height / cam.zoom;
    const pad = 150;

    const minSX = b.minX - pad;
    const minSY = b.minY - pad;
    const maxSX = b.maxX - viewW + pad;
    const maxSY = b.maxY - viewH + pad;

    if (maxSX < minSX) {
      cam.scrollX = (b.minX + b.maxX) / 2 - viewW / 2;
    } else {
      cam.scrollX = Phaser.Math.Clamp(cam.scrollX, minSX, maxSX);
    }
    if (maxSY < minSY) {
      cam.scrollY = (b.minY + b.maxY) / 2 - viewH / 2;
    } else {
      cam.scrollY = Phaser.Math.Clamp(cam.scrollY, minSY, maxSY);
    }
  }

  // ─── Building / Panel ──────────────────────────────────────

  private handleMapClick(px: number, py: number): void {
    console.log(`[MAP_CLICK] screen=(${px.toFixed(0)}, ${py.toFixed(0)})`);

    // Clicking on the panel area — let the panel handle it, do nothing here
    if (this.isPointerOverPanel(px)) {
      console.log(`[MAP_CLICK] Click is over panel area, ignoring`);
      return;
    }

    // If panel is open and we clicked outside it — close it
    if (this.sidePanel.isVisible()) {
      console.log(`[MAP_CLICK] Panel is open, closing it`);
      this.sidePanel.close();
      return;
    }

    // Find building under cursor using 2D projected bounding box
    // (accounts for building height — clicking anywhere on the visible building works)
    const worldPoint = this.cameras.main.getWorldPoint(px, py);
    const wx = worldPoint.x;
    const wy = worldPoint.y;
    const grid = this.isoMap.isoToGrid(wx, wy);
    console.log(`[MAP_CLICK] world=(${wx.toFixed(0)}, ${wy.toFixed(0)}) grid=(${grid.gridX}, ${grid.gridY})`);

    // Check buildings from front to back (higher depth = closer to camera = checked first)
    // Sort by depth descending so front buildings take priority
    const sorted = [...this.isoBuildings]
      .filter(ib => ib.building.interactable)
      .sort((a, b) =>
        (b.building.gridX + b.building.gridY) - (a.building.gridX + a.building.gridY),
      );

    for (const isoBuilding of sorted) {
      if (this.isPointInBuilding(wx, wy, isoBuilding)) {
        const b = isoBuilding.building;
        console.log(`[MAP_CLICK] Building found: ${b.name} (id=${b.id}, type=${b.type})`);
        socketManager.sendAction('ENTER_BUILDING', { buildingId: b.id });
        this.sidePanel.open(b);
        return;
      }
    }
    console.log(`[MAP_CLICK] No building at grid (${grid.gridX}, ${grid.gridY})`);
  }

  private isPointInBuilding(wx: number, wy: number, isoBuilding: IsoBuilding): boolean {
    const b = isoBuilding.building;
    const { x: cx, y: cy } = this.isoMap.gridToIso(b.gridX, b.gridY);
    const hw = 24; // half-width of iso footprint (same as IsoBuilding)
    const hh = 12; // half-height of iso footprint
    const height = this.getBuildingHeight(b.type);

    // Check if point is within the 2D bounding box of the building's visible area
    // The building occupies from (cx-hw, cy-hh-height) to (cx+hw, cy+hh) in world coords
    if (wx < cx - hw || wx > cx + hw) return false;
    if (wy < cy - hh - height || wy > cy + hh) return false;

    // More precise: check if inside the iso diamond footprint OR the building faces
    // Footprint diamond: |wx-cx|/hw + |wy-cy|/hh <= 1
    const inFootprint = Math.abs(wx - cx) / hw + Math.abs(wy - cy) / hh <= 1;
    // Left face: x in [cx-hw, cx], y in [cy-height, cy+hh]
    const inLeftFace = wx >= cx - hw && wx <= cx && wy >= cy - height && wy <= cy + hh;
    // Right face: x in [cx, cx+hw], y in [cy-height, cy+hh]
    const inRightFace = wx >= cx && wx <= cx + hw && wy >= cy - height && wy <= cy + hh;
    // Top face: same as footprint but shifted up by height
    const topCy = cy - height;
    const inTopFace = Math.abs(wx - cx) / hw + Math.abs(wy - topCy) / hh <= 1;

    const hit = inFootprint || inLeftFace || inRightFace || inTopFace;
    if (hit) {
      console.log(`[MAP_CLICK] isPointInBuilding HIT: ${b.name} (id=${b.id}) center=(${cx.toFixed(0)},${cy.toFixed(0)}) height=${height}`);
    }
    return hit;
  }

  private getBuildingHeight(type: string): number {
    const heights: Record<string, number> = {
      HOUSE: 40, APARTMENT: 70, SHOP: 35, POLICE_STATION: 55,
      WAREHOUSE: 45, PARK: 10, CASINO: 80, DEAL_POINT: 25, WHOLESALER: 50,
    };
    return heights[type] ?? 30;
  }

  private buyBuilding(building: BuildingData): void {
    console.log(`[MAP_CLICK] buyBuilding: ${building.name} (id=${building.id}, type=${building.type})`);
    socketManager.sendAction('BUY_BUILDING', { buildingId: building.id });
    // Use onceActionResult to avoid handler accumulation
    socketManager.onceActionResult((result) => {
      if (result.success) {
        building.ownerId = 'owned';
        // Request fresh building state and reopen panel
        socketManager.sendAction('ENTER_BUILDING', { buildingId: building.id });
        this.sidePanel.open(building);
      } else {
        this.sidePanel.open(building);
      }
    });
  }

  private enterBuilding(building: BuildingData): void {
    console.log(`[MAP_CLICK] enterBuilding: ${building.name} (id=${building.id}, type=${building.type})`);
    const buildingPos = this.isoMap.gridToIso(building.gridX, building.gridY);
    const cam = this.cameras.main;

    this.tweens.add({
      targets: cam,
      zoom: 2,
      scrollX: buildingPos.x - cam.width / 4,
      scrollY: buildingPos.y - cam.height / 4,
      duration: 400,
      ease: 'Power2',
      onComplete: () => {
        this.scene.pause();
        this.sidePanel.close();
        this.scene.launch('InteriorScene', {
          building,
          stateData: this.pendingBuildingState,
        });
      },
    });
  }

  // ─── Tick Handling ─────────────────────────────────────────

  private handleTick(data: WorldTickData): void {
    this.lastTickTime = Date.now();
    this.hud.updateTime(data.gameTime, data.dayPhase);
    this.updateDayNightOverlay(data.dayPhase);
    this.updateEntities(data.entities);
  }

  private updateDayNightOverlay(phase: string): void {
    switch (phase) {
      case 'DAY':   this.dayOverlay.setFillStyle(0x000000, 0);    break;
      case 'DUSK':  this.dayOverlay.setFillStyle(0xff6b35, 0.25); break;
      case 'NIGHT': this.dayOverlay.setFillStyle(0x0a0a2e, 0.6);  break;
      case 'DAWN':  this.dayOverlay.setFillStyle(0xffb347, 0.15); break;
    }
    for (const building of this.isoBuildings) {
      if (phase === 'NIGHT') building.showLabel();
      else building.hideLabel();
    }
  }

  private updateEntities(entities: GameEntity[]): void {
    const seenVehicles = new Set<string>();
    const seenPedestrians = new Set<string>();

    for (const entity of entities) {
      if (entity.type === 'VEHICLE' || entity.type === 'POLICE_CAR') {
        seenVehicles.add(entity.id);
        let vehicle = this.vehicles.get(entity.id);
        if (!vehicle) {
          vehicle = new IsoVehicle(this, entity, this.isoMap);
          this.vehicles.set(entity.id, vehicle);
          this.uiCamera.ignore(vehicle.getGraphics());
        }
        vehicle.updateTarget(entity);
      } else if (entity.type === 'PEDESTRIAN') {
        seenPedestrians.add(entity.id);
        let pedestrian = this.pedestrians.get(entity.id);
        if (!pedestrian) {
          pedestrian = new IsoPedestrian(this, entity, this.isoMap);
          this.pedestrians.set(entity.id, pedestrian);
          this.uiCamera.ignore(pedestrian.getGraphics());
        }
        pedestrian.updateTarget(entity);
      }
    }

    for (const [id, vehicle] of this.vehicles) {
      if (!seenVehicles.has(id)) {
        vehicle.destroy();
        this.vehicles.delete(id);
      }
    }
    for (const [id, pedestrian] of this.pedestrians) {
      if (!seenPedestrians.has(id)) {
        pedestrian.destroy();
        this.pedestrians.delete(id);
      }
    }
  }

  // ─── Update Loop (every frame) ─────────────────────────────

  update(_time: number, _delta: number): void {
    if (!this.isoMap) return;

    // ── Drag-to-scroll via pointer polling ──
    const pointer = this.input.activePointer;
    const isDown = pointer.isDown && !pointer.rightButtonDown();

    if (isDown && !this.pointerWasDown) {
      // Pointer just pressed
      if (this.isPointerOverPanel(pointer.x)) {
        // Over the panel — block drag, let panel buttons work
        this.dragBlocked = true;
      } else {
        this.dragBlocked = false;
        this.dragStartPointerX = pointer.x;
        this.dragStartPointerY = pointer.y;
        this.dragStartScrollX = this.cameras.main.scrollX;
        this.dragStartScrollY = this.cameras.main.scrollY;
        this.dragTotalDistance = 0;
      }
    } else if (isDown && this.pointerWasDown && !this.dragBlocked) {
      // Pointer held — drag the map
      const dx = pointer.x - this.dragStartPointerX;
      const dy = pointer.y - this.dragStartPointerY;
      this.dragTotalDistance = Math.sqrt(dx * dx + dy * dy);

      if (this.dragTotalDistance > DRAG_THRESHOLD) {
        const zoom = this.cameras.main.zoom;
        this.cameras.main.scrollX = this.dragStartScrollX - dx / zoom;
        this.cameras.main.scrollY = this.dragStartScrollY - dy / zoom;
        this.clampCamera();
      }
    } else if (!isDown && this.pointerWasDown) {
      // Pointer just released
      console.log(`[POINTER] Released, dragDistance=${this.dragTotalDistance.toFixed(1)}, threshold=${DRAG_THRESHOLD}, blocked=${this.dragBlocked}`);
      if (!this.dragBlocked && this.dragTotalDistance <= DRAG_THRESHOLD) {
        this.handleMapClick(pointer.x, pointer.y);
      }
      this.dragBlocked = false;
    }

    this.pointerWasDown = isDown;

    // ── Interpolate entities ──
    const elapsed = Date.now() - this.lastTickTime;
    const t = Math.min(elapsed / this.tickInterval, 1);

    for (const vehicle of this.vehicles.values()) {
      vehicle.interpolate(t);
    }
    for (const pedestrian of this.pedestrians.values()) {
      pedestrian.interpolate(t);
    }

    // ── Update minimap ──
    const cam = this.cameras.main;
    const b = this.worldBounds;
    this.minimap.updateCameraPosition(
      cam.scrollX - b.minX,
      cam.scrollY - b.minY,
      cam.width / cam.zoom,
      cam.height / cam.zoom,
      b.maxX - b.minX,
      b.maxY - b.minY,
    );
  }
}
